<template>
<div>
  <b-row class="timesheets-details mt-3" v-if="toResetSTD.length" >
    <button
        class="btn bg-warning text-white border-radius-5 font-weight-bold "
        @click="resetStd"
    >{{ $t($getConst.pages.timesheet.reset_std) }}</button>
  </b-row>
  <b-row class="timesheets-details mt-3">
    <b-col class="xxl-9 overflow-auto">
      <calendar
        class="calendar"
        :masks="$screens({ default: {weekdays: 'WWW'}, lg: {weekdays: 'WWWW'} })"
        :locale="this.$i18n.locale"
        :from-page="{month: this.timesheetMonth, year: this.timesheetYear}"
        :attributes="attributes"
        disable-page-swipe
        :trim-weeks="$screens({ default: true, lg: false })"
      >

        <template #header>
          <div></div>
        </template>
        <template #header-left-button>
          <div></div>
        </template>
        <template #header-right-button>
          <div></div>
        </template>

        <template v-slot:day-content="{ day, attributes }">
          <template v-if="attributes && attributes.length">
            <template v-for="attr in attributes">
              <div
                type="button"
                @click="selectDay(attr.customData.date, attr.key, $event)"
                class="h-100 d-flex flex-column px-2 pb-2"
                :class="[
                  {'selected-reset-std': toResetSTD.includes(attr.key)},
                  {'selected-day': attr.customData.date === selectedDate},
                  {'bg-light': attr.customData.isHoliday === 'Yes' && attr.customData.date !== selectedDate}
                ]"
              >
                <div class="d-flex">
                  <p class="text-warning font-weight-bold mb-0 pr-1 pr-md-2">
                    <i v-if="attr.customData.Remarks" :class="[$getConst.icons.comment, 'mr-1']"></i>
                    <i v-if="hasOvertime(attr.customData)" :class="$getConst.icons.exclamation_circle"></i>
                  </p>
                  <p class="font-weight-bold text-dark ml-auto mb-0 pr-1 pr-md-2">{{ day.day }}</p>
                </div>
                <p
                  v-if="attr.customData.isHoliday === 'Yes'"
                  class="hours font-weight-bold d-none d-sm-inline-block mx-1 badge-pill my-auto font-size-sm font-weight-600"
                  :class="dayBadgeClass"
                >
                  <template v-if="isDayFilled(attr.customData.std)">
                    {{ totalWorkedDay(attr.customData) }}
                    <span v-if="timesheet.Unit.toLowerCase() === 'hour'">hrs</span>
                    <span v-else-if="timesheet.Unit.toLowerCase() === 'day'">days</span>
                  </template>
                </p>
                <p
                  v-else-if="attr.customData.absence === '0'"
                  class="hours font-weight-bold text-white py-1 px-0 mx-1 badge-pill my-auto line-height-sm font-size-sm font-weight-600"
                  :class="dayBadgeClass"
                >
                  {{ totalWorkedDay(attr.customData) }}
                  <span v-if="timesheet.Unit.toLowerCase() === 'hour'">hrs</span>
                  <span v-else-if="timesheet.Unit.toLowerCase() === 'day'">days</span>
                </p>

                <template v-else>
                  <p
                    v-if="!(isApprover('L1'))"
                    class="d-flex hours font-weight-bold badge-warning mx-1 badge-pill my-auto mx-1 text-uppercase font-size-sm font-weight-600 text-white"
                    :class="dayBadgeClass" style="justify-content: space-evenly"
                  >
                    <span v-if="parseFloat(attr.customData.absenceQty) == 0.5"
                          class="font-size-md my-auto text-center"><sup>1</sup>&frasl;<sub>2</sub></span>
                    <span v-if="isAbsenceManagement" class="text-center">{{ getAbsenceDescription(attr.customData.absenceCode) }}</span>
                    <span v-else class="text-center"> {{attr.customData.absenceCode}}</span>
                  </p>
                  <p
                    v-if="parseFloat(attr.customData.absenceQty) != '0' "
                    class="hours font-weight-bold text-white py-1 px-0 mx-1 badge-pill my-auto line-height-sm font-size-sm font-weight-600"
                    :class="dayBadgeClass"
                  >
                    {{ totalWorkedDay(attr.customData) }}
                    <span v-if="timesheet.Unit.toLowerCase() === 'hour'">hrs</span>
                    <span v-else-if="timesheet.Unit.toLowerCase() === 'day'">days</span>
                  </p>
                </template>
              </div>
            </template>
          </template>
          <div v-else class="h-100 d-flex flex-column">
            <p class="font-weight-bold text-dark text-right mb-0 pr-1 pr-md-2">{{ day.day }}</p>
          </div>
        </template>

      </calendar>
      <span class="text-info">{{ $t($getConst.pages.timesheet.reset_info) }}</span>
    </b-col>

    <b-col id="timesheet-form" class="xxl-3">
      <TimesheetForm
        v-if="selectedDate"
        v-model="timesheet"
        :date="selectedDate"
        :day-index="selectedDayIndex"
        :is-mobile-view="isMobileView"
        :isAbsencesPerDays="isAbsencesPerDays"
        :isWorkedHoursValidation="isWorkedHoursValidation"
        @closeDayForm="deselectDay"
        :timesheetStd="timesheetStd"
        :isAbsenceManagement="isAbsenceManagement"
        :unitAbsences="unitAbsences"
      />
      <TimesheetTotalHours
        v-else
        :timesheet="timesheet"
        :unitAbsences="unitAbsences"
        :timesheetStd="timesheetStd"
        submit-from-form
      />
    </b-col>
  </b-row>
</div>
</template>

<script>
  import TimesheetForm from "./TimesheetForm";
  import TimesheetTotalHours from "./TimesheetTotalHours";
  import Vue from "vue";
  import {getSmartWorking} from "../../../shared/services/timesheets";

  export default {
    name: "MonthlyTimesheet",
    components: {TimesheetTotalHours, TimesheetForm},
    props: {
      timesheet: null,
      isMobileView: {
        type: Boolean,
        default: false
      },
      timesheetStd: null
    },
    data() {
      return {
        masks: {
          weekdays: 'WWWW',
        },
        timesheetMonth: null,
        timesheetYear: null,
        approversNames: [],
        attributes: [],
        selectedDate: null,
        selectedDayIndex: null,
        updatedAbsence: [],
        isAbsencesPerDays:false,
        isWorkedHoursValidation:false,
        toResetSTD:[],
        unitAbsences:null,
        isLimitingHoursOfWork: false
      }
    },
    computed: {
      dayBadgeClass() {
        return this.isFreelancer() ? 'badge-secondary border-radius-5 ' : 'badge-success';
      },
      isAbsenceManagement() {
        return !!(this.timesheet.Days.Absences && this.timesheet.Days.Absences[0].Managed === "Yes");
      },
    },
    methods: {
      hasOvertime(dayData) {
        return parseFloat(dayData.ot1) > 0 || parseFloat(dayData.ot2) > 0 || parseFloat(dayData.ot3) > 0;
      },
      isDayFilled(std) {
        return parseFloat(std) > 0;
      },
      checkTotalHoursPerDay(totalHoursPerDay, date, index,totalHoursStdPerDay, isHoliday) {
        if(totalHoursStdPerDay !== this.timesheetStd && this.isLimitingHoursOfWork && this.isWorker() && isHoliday==="No") {
          Vue.$snotify.error(this.$t(this.$getConst.pages.alerts.invalid_total_std_hours))
        } else if (totalHoursStdPerDay > this.timesheetStd && this.isFreelancer()) {
          Vue.$snotify.error(this.$t(this.$getConst.pages.alerts.invalid_freelancer_hours_per_day))
        } else {
          if (totalHoursPerDay <= 24) {
            this.selectedDate = date;
            this.selectedDayIndex = index;
            const timesheetFormElement = document.getElementById("timesheet-form");
            timesheetFormElement.scrollIntoView();
          } else {
            Vue.$snotify.error(this.$t(this.$getConst.pages.alerts.invalid_hours_per_day))
          }
        }
      },
      selectDay(date, index, event) {

        if (this.selectedDayIndex !== null && this.selectedDayIndex !== undefined) {
          let dayInfo = this.timesheet.Days.Days[this.selectedDayIndex]
          let totalHoursPerDay = parseFloat(dayInfo.absenceQty) + parseFloat(dayInfo.std) + parseFloat(dayInfo.ot1) + parseFloat(dayInfo.ot2) + parseFloat(dayInfo.ot3)
          let totalHoursStdPerDay = parseFloat(dayInfo.absenceQty) + parseFloat(dayInfo.std)
          if (getSmartWorking(this.timesheet)) {
            totalHoursStdPerDay += parseFloat(dayInfo[getSmartWorking(this.timesheet)])
          }
          this.checkTotalHoursPerDay(totalHoursPerDay, date, index, totalHoursStdPerDay, dayInfo.isHoliday);
        } else {
          this.selectedDate = date;
          this.selectedDayIndex = index;
          const timesheetFormElement = document.getElementById("timesheet-form");
          timesheetFormElement.scrollIntoView();
        }


        if (event.ctrlKey) {
          (this.toResetSTD.includes(this.selectedDayIndex) ? this.toResetSTD = this.toResetSTD.filter(e => e !== this.selectedDayIndex) : this.toResetSTD.push(this.selectedDayIndex))
        } else {
          this.toResetSTD = []
        }
      },
      resetStd(){
        this.toResetSTD.forEach((el)=>{
          this.timesheet.Days.Days[el].std = 0
        })
        this.toResetSTD = []
      },
      deselectDay() {
        let dayInfo = this.timesheet.Days.Days[this.selectedDayIndex]
        let totalHoursStdPerDay = parseFloat(dayInfo.absenceQty) + parseFloat(dayInfo.std)
        let totalHoursPerDay = parseFloat(dayInfo.absenceQty) + parseFloat(dayInfo.std) + parseFloat(dayInfo.ot1) + parseFloat(dayInfo.ot2) + parseFloat(dayInfo.ot3)
        if (getSmartWorking(this.timesheet)) {
          totalHoursStdPerDay += parseFloat(dayInfo[getSmartWorking(this.timesheet)])
        }
        this.checkTotalHoursPerDay(totalHoursPerDay, null, null, totalHoursStdPerDay, dayInfo.isHoliday);
      },
      getAbsenceDescription(absenceCode) {
        const absenceType = this.timesheet.Days.Absences[0].AbsencesList.find(
          element => element.code === absenceCode
        );

        return absenceType.Description;
      },
      getTimesheet() {
        this.timesheetMonth = Number(this.timesheet.TimesheetStartDate.split("/")[1]);
        this.timesheetYear = this.timesheet.TimesheetStartDate.split("/")[2];
        this.formatDays();
        this.timesheet.Approvers.forEach(approver => {
          if (approver.id > 0) {
            this.approversNames.push(approver.name);
          }
        })
      },
      formatDays() {
        this.timesheet.Days.Days.forEach((day, key) => {
          let formattedDay = {};
          formattedDay.customData = {};
          const dateParts = day.date.split("/");
          formattedDay.key = key;
          formattedDay.dates = new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
          formattedDay.customData = day;
          this.attributes.push(formattedDay);
        })
      },
      totalWorkedDay(customData) {
        let total = parseFloat(customData.std)
        if (this.hasOvertime(customData)) {
          total += parseFloat(customData.ot1) + parseFloat(customData.ot2) + parseFloat(customData.ot3)
          /*
                    if (this.timesheet.Unit.toLowerCase() === 'hour' && parseFloat(total.toString().split('.')[1]) ) {
                      total = this.concatenateHoursAndMinutes(Math.trunc(total), parseFloat(total.toFixed(2).toString().split('.')[1]))
                    }
          */
        }
        return total
      },
      concatenateHoursAndMinutes(hours, minutes) {
        //Convert Minutes
        let fromHoursToMinutes = Math.round(minutes * 60 / 100).toString().length == 1
          ? `0${Math.round(minutes * 60 / 100).toString()}`
          : (Math.round(minutes * 60 / 100)).toString();

        return `${hours}.${fromHoursToMinutes}`;
      }
    },
    created() {
      this.getTimesheet();
      if (this.timesheet.Days.Absences[0].Managed === 'No'){
        this.absenceManeged =false;
      }
    },
    mounted() {
      if (this.$store.getters['auth/scopeDefault'].is_absences_per_days === true) {
        this.isAbsencesPerDays = true;
      }
      if (this.$store.getters['auth/scopeDefault'].is_worked_hours_validation === true) {
        this.isWorkedHoursValidation = true;
      }
      if (this.$store.getters['auth/scopeDefault'].is_limiting_hours_of_work === true) {
        this.isLimitingHoursOfWork = true;
      }
     this.unitAbsences = this.isAbsencesPerDays?'day':this.timesheet.Unit.toLowerCase();
    }
  }
</script>

<style scoped>
  .border-radius-5 {
    border-radius: 5px !important;
  }
</style>
